import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
  createAction,
} from '@reduxjs/toolkit';
import { getTemperatureAndHumidity } from 'scenes/Renolog/services/getTemperatureAndHumidity.service';
import { getMouldRisk } from 'scenes/Renolog/services/getMouldRisk.service';
import { getRenologDevices } from 'scenes/Renolog/services/getRenologDevices.service';
import { getSensorOrders } from 'scenes/Renolog/services/getSensorOrders.service';
import { cancelSensorOrder as cancelSensorOrderService } from 'scenes/Renolog/services/cancelSensorOrder.service';

const deviceAdapter = createEntityAdapter({
  selectId: (entry) => entry.deviceId,
});

const initialProjectState = () => ({
  ...deviceAdapter.getInitialState(),
  sensorReadings: {},
  sensorOrders: [],
  loading: {
    devices: false,
    sensorData: {},
    sensorOrders: false,
  },
  loadingStarted: {
    devices: false,
    sensorData: {},
    sensorOrders: false,
  },
  error: {
    devices: undefined,
    sensorData: {},
    sensorOrders: undefined,
  },
});

const ensureProject = (state, projectId) => {
  if (!state.byProject[projectId]) {
    state.byProject[projectId] = initialProjectState();
  }
};

export const sensorDataCleared = createAction(
  'renologSensorData/sensorDataCleared',
  ({ projectId, deviceId }) => ({ payload: { projectId, deviceId } })
);

export const sensorDataBatchReceived = createAction(
  'renologSensorData/sensorDataBatchReceived',
  ({ projectId, deviceId, temperatureHumidityBatch, mouldRiskBatch }) => ({
    payload: { projectId, deviceId, temperatureHumidityBatch, mouldRiskBatch },
  })
);

const fetchAllPages = async (fetchFn, onBatch) => {
  let lastKey;
  do {
    const result = await fetchFn(lastKey);
    onBatch(result.items);
    lastKey = result.lastEvaluatedKey || undefined;
  } while (lastKey);
};

export const fetchSensorData = createAsyncThunk(
  'renolog/fetchSensorData',
  async ({ projectId, deviceId }, { rejectWithValue, dispatch }) => {
    try {
      dispatch(sensorDataCleared({ projectId, deviceId }));

      await Promise.all([
        fetchAllPages(
          (lastKey) => getTemperatureAndHumidity(projectId, deviceId, lastKey),
          (items) =>
            dispatch(
              sensorDataBatchReceived({
                projectId,
                deviceId,
                temperatureHumidityBatch: items,
              })
            )
        ),
        fetchAllPages(
          (lastKey) => getMouldRisk(projectId, deviceId, lastKey),
          (items) =>
            dispatch(
              sensorDataBatchReceived({
                projectId,
                deviceId,
                mouldRiskBatch: items,
              })
            )
        ),
      ]);

      return { projectId, deviceId };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch sensor data');
    }
  }
);

export const fetchSensorOrders = createAsyncThunk(
  'renolog/fetchSensorOrders',
  async ({ projectId }, { rejectWithValue }) => {
    try {
      const orders = await getSensorOrders(projectId);
      return { orders, projectId };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch sensor orders');
    }
  }
);

export const cancelSensorOrder = createAsyncThunk(
  'renolog/cancelSensorOrder',
  async ({ projectId, orderId }, { rejectWithValue }) => {
    try {
      await cancelSensorOrderService(projectId, orderId);
      return { projectId, orderId };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to cancel sensor order');
    }
  }
);

export const fetchDevices = createAsyncThunk(
  'renolog/fetchDevices',
  async ({ projectId, doSync }, { rejectWithValue }) => {
    try {
      const devices = await getRenologDevices(projectId, doSync);
      return { devices, projectId, doSync };
    } catch (error) {
      return rejectWithValue(error.message || 'Failed to fetch devices');
    }
  }
);

const initialState = {
  byProject: {},
};

const sensorDataSlice = createSlice({
  name: 'renologSensorData',
  initialState,
  reducers: {
    sensorOrderAdded(state, action) {
      const { projectId, order } = action.payload;
      ensureProject(state, projectId);
      state.byProject[projectId].sensorOrders.push(order);
    },
    sensorOrderUpdated(state, action) {
      const { projectId, order } = action.payload;
      console.log('Updating sensor order in state', { projectId, order });
      if (!state.byProject[projectId]) return;
      const index = state.byProject[projectId].sensorOrders.findIndex(
        (o) => o.id === order.id
      );
      if (index !== -1) {
        state.byProject[projectId].sensorOrders[index] = order;
      }
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(sensorDataCleared, (state, action) => {
        const { projectId, deviceId } = action.payload;
        ensureProject(state, projectId);
        state.byProject[projectId].sensorReadings[deviceId] = {
          temperatureHumidity: [],
          mouldRisk: [],
        };
      })
      .addCase(sensorDataBatchReceived, (state, action) => {
        const {
          projectId,
          deviceId,
          temperatureHumidityBatch,
          mouldRiskBatch,
        } = action.payload;
        const project = state.byProject[projectId];
        if (!project) return;
        const readings = project.sensorReadings[deviceId];
        if (!readings) return;
        if (temperatureHumidityBatch) {
          readings.temperatureHumidity.push(...temperatureHumidityBatch);
        }
        if (mouldRiskBatch) {
          readings.mouldRisk.push(...mouldRiskBatch);
        }
      })
      .addCase(fetchDevices.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        ensureProject(state, projectId);
        state.byProject[projectId].loading.devices = true;
        state.byProject[projectId].loadingStarted.devices = true;
        state.byProject[projectId].error.devices = undefined;
      })
      .addCase(fetchDevices.fulfilled, (state, action) => {
        const { devices, projectId } = action.payload;
        ensureProject(state, projectId);
        deviceAdapter.setAll(state.byProject[projectId], devices);
        state.byProject[projectId].loading.devices = false;
        state.byProject[projectId].error.devices = undefined;
      })
      .addCase(fetchDevices.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        if (!state.byProject[projectId]) return;
        state.byProject[projectId].loading.devices = false;
        state.byProject[projectId].error.devices =
          action.payload || 'Unknown error';
      })
      .addCase(fetchSensorOrders.pending, (state, action) => {
        const { projectId } = action.meta.arg;
        ensureProject(state, projectId);
        state.byProject[projectId].loading.sensorOrders = true;
        state.byProject[projectId].loadingStarted.sensorOrders = true;
        state.byProject[projectId].error.sensorOrders = undefined;
      })
      .addCase(fetchSensorOrders.fulfilled, (state, action) => {
        const { orders, projectId } = action.payload;
        ensureProject(state, projectId);
        state.byProject[projectId].sensorOrders = orders;
        state.byProject[projectId].loading.sensorOrders = false;
        state.byProject[projectId].error.sensorOrders = undefined;
      })
      .addCase(fetchSensorOrders.rejected, (state, action) => {
        const { projectId } = action.meta.arg;
        if (!state.byProject[projectId]) return;
        state.byProject[projectId].loading.sensorOrders = false;
        state.byProject[projectId].error.sensorOrders =
          action.payload || 'Unknown error';
      })
      .addCase(fetchSensorData.pending, (state, action) => {
        const { projectId, deviceId } = action.meta.arg;
        ensureProject(state, projectId);
        state.byProject[projectId].loading.sensorData[deviceId] = true;
        state.byProject[projectId].loadingStarted.sensorData[deviceId] = true;
        state.byProject[projectId].error.sensorData[deviceId] = undefined;
      })
      .addCase(fetchSensorData.fulfilled, (state, action) => {
        const { projectId, deviceId } = action.payload;
        if (!state.byProject[projectId]) return;
        state.byProject[projectId].loading.sensorData[deviceId] = false;
        state.byProject[projectId].error.sensorData[deviceId] = undefined;
      })
      .addCase(fetchSensorData.rejected, (state, action) => {
        const { projectId, deviceId } = action.meta.arg;
        if (!state.byProject[projectId]) return;
        state.byProject[projectId].loading.sensorData[deviceId] = false;
        state.byProject[projectId].error.sensorData[deviceId] =
          action.payload || 'Unknown error';
      })
      .addCase(cancelSensorOrder.fulfilled, (state, action) => {
        const { projectId, orderId } = action.payload;
        if (!state.byProject[projectId]) return;
        state.byProject[projectId].sensorOrders = state.byProject[
          projectId
        ].sensorOrders.filter((o) => o.id !== orderId);
      });
  },
});

export const { sensorOrderAdded, sensorOrderUpdated } = sensorDataSlice.actions;

export const selectDevicesForProject = (state, projectId) =>
  Object.values(state.renologSensorData.byProject[projectId]?.entities || {});

export const selectDeviceById = (state, projectId, deviceId) =>
  state.renologSensorData.byProject[projectId]?.entities?.[deviceId];

export const selectSensorReadings = (state, projectId, deviceId) =>
  state.renologSensorData.byProject[projectId]?.sensorReadings?.[deviceId];

export const selectDeviceLoading = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.loading?.devices ?? false;

export const selectDevicesLoadingStarted = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.loadingStarted?.devices ??
  false;

export const selectSensorDataLoading = (state, projectId, deviceId) =>
  state.renologSensorData.byProject[projectId]?.loading?.sensorData?.[
    deviceId
  ] ?? false;

export const selectSensorDataLoadingStarted = (state, projectId, deviceId) =>
  state.renologSensorData.byProject[projectId]?.loadingStarted?.sensorData?.[
    deviceId
  ] ?? false;

export const selectSensorDataLoadingStartedForProject = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.loadingStarted?.sensorData ||
  {};

export const selectSensorOrders = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.sensorOrders || [];

export const selectSensorOrdersLoadingStarted = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.loadingStarted?.sensorOrders ??
  false;

export const selectDeviceError = (state, projectId) =>
  state.renologSensorData.byProject[projectId]?.error?.devices;

export const selectSensorDataError = (state, projectId, deviceId) =>
  state.renologSensorData.byProject[projectId]?.error?.sensorData?.[deviceId];

export default sensorDataSlice.reducer;
